import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';

// BlockPanel — the building-grid tool's controls.
//
// The BRUSH is whatever you last shift-clicked, which means the palette is the
// entire city model and copying a building is just clicking it. There is no
// asset list to maintain and a pasted building can never look foreign, because
// it is literally the same mesh.
//
// Pastes are UNSAVED until you press Save. Filling a block is a dozen clicks and
// most of them are wrong at first, so committing each one immediately would mean
// a dozen rows to clean up; this way a bad run is discarded by walking away.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 214, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};

export default function BlockPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'blocks';
  const picked = useWorldStore((s) => s.picked);
  const objects = useWorldStore((s) => s.worldObjects);
  const dirty = useWorldStore((s) => s.worldObjectDirty);
  const removeObj = useWorldStore((s) => s.removeWorldObject);
  const markSaved = useWorldStore((s) => s.markWorldObjectSaved);
  const patch = useWorldStore((s) => s.patchWorldObject);
  const rot = useWorldStore((s) => s.blockRot);
  const turn = useWorldStore((s) => s.turnBlock);
  const last = useWorldStore((s) => s.blockLast);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const setSel = useWorldStore((s) => s.setWorldObjectSel);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!ON) return null;

  const mine = objects.filter((o) => o.zone_id === 'street' && o.kind === 'building');
  const unsaved = mine.filter((o) => dirty.includes(o.object_id));
  const cur = mine.find((o) => o.object_id === sel) || null;

  // Delete the SELECTED building — removes the row when it has one, and drops it
  // from the scene either way.
  const del = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      if (typeof cur.object_id === 'number') await deleteWorldObject(cur.object_id);
      removeObj(cur.object_id);
      setSel(null);
      setMsg('deleted');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const undo = async () => {
    const last = mine[mine.length - 1];
    if (!last) return;
    setBusy(true);
    try {
      if (typeof last.object_id === 'number') await deleteWorldObject(last.object_id);
      removeObj(last.object_id);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const saveAll = async () => {
    if (!unsaved.length) return;
    setBusy(true); setMsg('');
    try {
      for (const o of unsaved) {
        const body = {
          zone_id: 'street', kind: 'building', ref: o.ref, slot: null,
          grid: o.grid, pos: o.pos, rotY: o.rotY, size: o.size,
        };
        // A string id means it has never been a row. Editing an existing
        // building has to PATCH — creating again would leave the original in
        // place and stack a second copy on top of it.
        const isNew = typeof o.object_id === 'string';
        const saved = isNew ? await createWorldObject(body) : await updateWorldObject(o.object_id, body);
        if (saved?.object_id) { patch(o.object_id, { object_id: saved.object_id }); markSaved(o.object_id); markSaved(saved.object_id); }
      }
      setMsg(`saved ${unsaved.length}`);
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>
        Building grid
      </div>

      <div style={{ fontSize: 10.5, opacity: 0.6, marginBottom: 4 }}>Brush — shift-click a building (Esc to change)</div>
      <div style={{
        fontSize: 11, padding: '7px 8px', borderRadius: 8, marginBottom: 10, wordBreak: 'break-all',
        background: picked?.name ? 'rgba(157,255,125,0.12)' : 'rgba(255,178,94,0.1)',
        border: `1px solid ${picked?.name ? 'rgba(157,255,125,0.4)' : 'rgba(255,178,94,0.3)'}`,
        color: picked?.name ? '#c8ffb8' : '#ffd27d',
      }}>
        {picked?.name || 'nothing picked — shift-click any building'}
      </div>

      <div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => turn(-1)}>⟲ 90°</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => turn(1)}>90° ⟳</button>
      </div>
      <div style={{ opacity: 0.5, fontSize: 10, marginBottom: 10, fontVariantNumeric: 'tabular-nums' }}>
        rotation {Math.round((rot * 180) / Math.PI) % 360}°
      </div>

      <div style={{ opacity: 0.7, fontSize: 11, marginBottom: 6 }}>
        {mine.length} placed · {unsaved.length} unsaved
      </div>

      {/* Placed list. Selecting from a LIST rather than by clicking in the world
          because a click on the map already means paste — picking a building to
          edit by clicking it would place another one on top of the one you were
          trying to select. */}
      {mine.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 110, overflowY: 'auto', marginBottom: 8 }}>
          {mine.map((o) => (
            <button key={o.object_id} onClick={() => setSel(o.object_id === sel ? null : o.object_id)}
              style={{ ...btn, fontSize: 10.5, padding: '5px 7px', textAlign: 'left',
                background: o.object_id === sel ? '#ffb25e' : 'rgba(255,178,94,0.14)',
                color: o.object_id === sel ? '#1c1206' : '#ffd27d' }}>
              {(o.grid ? `${o.grid[0]},${o.grid[1]}` : '—')} · {String(o.ref).slice(-22)}{dirty.includes(o.object_id) ? ' •' : ''}
            </button>
          ))}
        </div>
      )}

      {cur && (
        <div style={{ borderTop: '1px solid rgba(255,178,94,0.2)', paddingTop: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 5 }}>Selected · exact position</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
            {['x', 'y', 'z'].map((axis, i) => (
              <label key={axis} style={{ flex: 1, fontSize: 10, opacity: 0.75 }}>
                {axis.toUpperCase()}
                <input
                  type="number" step="0.5" value={cur.pos[i]}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!Number.isFinite(v)) return;
                    const next = [...cur.pos]; next[i] = v;
                    patch(cur.object_id, { pos: next });
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: '4px 5px', borderRadius: 6,
                    background: 'rgba(255,178,94,0.1)', color: '#ffd27d', border: '1px solid rgba(255,178,94,0.35)', font: '11px system-ui' }}
                />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => patch(cur.object_id, { rotY: (cur.rotY || 0) - Math.PI / 2 })}>⟲ 90°</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => patch(cur.object_id, { rotY: (cur.rotY || 0) + Math.PI / 2 })}>90° ⟳</button>
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => patch(cur.object_id, { rotY: (cur.rotY || 0) - 0.05 })}>⟲ fine</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => patch(cur.object_id, { rotY: (cur.rotY || 0) + 0.05 })}>fine ⟳</button>
          </div>
          <div style={{ opacity: 0.5, fontSize: 10, margin: '6px 0', fontVariantNumeric: 'tabular-nums' }}>
            rotation {Math.round(((cur.rotY || 0) * 180) / Math.PI)}°
          </div>
          <button style={{ ...btn, width: '100%', background: 'rgba(255,120,120,0.16)', color: '#ff9d9d', borderColor: 'rgba(255,120,120,0.4)' }}
            onClick={del} disabled={busy}>Delete this building</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 5 }}>
        <button disabled={busy || !unsaved.length}
          style={{ ...btn, flex: 1, background: unsaved.length ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: unsaved.length ? '#1c1206' : '#ffd27d' }}
          onClick={saveAll}>{busy ? '…' : `Save ${unsaved.length || ''}`}</button>
        <button style={{ ...btn, width: 62 }} onClick={undo} disabled={busy || !mine.length}>undo</button>
      </div>
      {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85, color: /saved/.test(msg) ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}

      {/* LAST CLICK. Every early return in the paste handler reports itself here,
          so "nothing happened" always names its own reason instead of needing
          another round of guessing. */}
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,178,94,0.2)' }}>
        <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 3 }}>Last click</div>
        <div style={{ fontSize: 10.5, color: last ? (last.ok ? '#9dff7d' : '#ff9d7d') : '#ffe9c4', opacity: last ? 0.95 : 0.5, wordBreak: 'break-all' }}>
          {!last ? 'nothing clicked yet'
            : last.ok ? `pasted at cell ${last.gx},${last.gz} → world ${last.x}, ${last.z}`
            : last.why}
        </div>
      </div>

      <div style={{ opacity: 0.55, fontSize: 10.5, marginTop: 8, lineHeight: 1.35 }}>
        Shift-click a building to pick it · shift-click a cell to paint · Esc to drop the brush and pick again. A plain click does nothing, so the camera stays free. Unsaved until you press Save.
      </div>
    </div>
  );
}
